import React from 'react';
import { StyleSheet } from 'react-native';
import { useSelector } from 'react-redux';
import SpaceUtilizationWidget from './SpaceUtilizationWidget';
import BarGraph from '../../../../graphs/BarGraph';
import { DateRange } from '../../../../redux/dateRangeSlice';
import LineGraph from '../../../../graphs/LineGraph';
import Colors from '../../../../constants/Colors';
import TimeIcon16 from '../../../16pxIcons/TimeIcon16';
import { ChartDataPoint } from '../../../../graphs/axisUtils';
import { getRangeHours } from '../../../../constants/Utils';

interface TimeComparisonGraphProps {
  hoursBookedData: ChartDataPoint[],
  timeSpentData: ChartDataPoint[],
  hoursBookedDataCompared: ChartDataPoint[],
  timeSpentDataCompared: ChartDataPoint[],
}

const dayKey = (dateLike: string) => {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return dateLike;
  return d.toISOString().slice(0, 10);
};

const alignTwoSeriesByDay = (a: ChartDataPoint[], b: ChartDataPoint[]) => {
  const aMap = new Map<string, ChartDataPoint>();
  const bMap = new Map<string, ChartDataPoint>();

  a.forEach((p) => aMap.set(dayKey(p.date), p));
  b.forEach((p) => bMap.set(dayKey(p.date), p));

  const allKeys = Array.from(new Set([...aMap.keys(), ...bMap.keys()]))
    .sort((k1, k2) => new Date(k1).getTime() - new Date(k2).getTime());

  const alignedA: ChartDataPoint[] = allKeys.map((k, index) => {
    const existing = aMap.get(k);
    return existing
      ? { ...existing, index }
      : { index, date: k, value: 0 };
  });

  const alignedB: ChartDataPoint[] = allKeys.map((k, index) => {
    const existing = bMap.get(k);
    return existing
      ? { ...existing, index }
      : { index, date: k, value: 0 };
  });

  return { alignedA, alignedB };
};

const TimeComparisonGraph = ({
  hoursBookedData: chartData, timeSpentData: alternateData,
  hoursBookedDataCompared: comparisonData, timeSpentDataCompared: alternateComparisonData,
}: TimeComparisonGraphProps) => {
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => state.dateRange);
  const compareRange: DateRange = useSelector((state:
    { dateComparisonRange: DateRange }) => (state.dateComparisonRange));
  const showBarGraph: boolean = !(!!compareRange.start && !!compareRange.end)
    && getRangeHours(range) / 24 < 8;
  const unit: string = getRangeHours(range) > 24 ? 'Hours' : 'Minutes';
  const title: string = 'Booked time';
  const alternateTitle: string = 'Time occupied';

  const {
    alignedA: paddedChartData,
    alignedB: paddedAlternateData,
  } = alignTwoSeriesByDay(chartData ?? [], alternateData ?? []);

  return <SpaceUtilizationWidget
    icon={<TimeIcon16 />}
    title='Booked time vs time spent'
    content={showBarGraph ? <BarGraph
      {...{
        unit, title, alternateTitle, range,
      }}
      chartData={paddedChartData}
      alternateData={paddedAlternateData}
      selectedColor={Colors.light.hereworksDarkBlue300}
      alternateColor={Colors.light.hereworksDarkBlue100}
      chartHeight={315}
      horizontalTickCount={12}
      style={styles.barGraph}
      showLabels={false}
      stackAlternate
      showLegend
      hideTimeOnTooltip
    /> : <LineGraph
      {...{
        unit,
        title,
        alternateTitle,
        chartData,
        alternateData,
        range,
        comparisonData,
        alternateComparisonData,
      }}
      alternateColor={Colors.light.hereworksBlue100}
      noTitle
      chartHeight={420}
      style={styles.lineGraph}
      showLegend
      hideTimeOnTooltip
      domainYLimit={24}
    />}
  />;
};

const styles = StyleSheet.create({
  barGraph: {
    marginStart: -12,
  },
  lineGraph: {
    marginTop: -70,
    marginStart: -24,
    marginEnd: 20,
  },
});

export default TimeComparisonGraph;
